import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useLocation } from 'react-router-dom';
import { trackPageView } from './utils/usageTracker';
import BugReportModal from './components/BugReportModal';
import HelpLauncher from './components/HelpLauncher';
import MyBugReports from './pages/MyBugReports';
import LifecycleSetup from './pages/lifecycle/LifecycleSetup';
import DocumentsAdmin from './pages/lifecycle/DocumentsAdmin';
import MyDocuments from './pages/lifecycle/MyDocuments';
import MyTasks from './pages/lifecycle/MyTasks';
import CasesAdmin from './pages/lifecycle/CasesAdmin';
import Welcome from './pages/lifecycle/Welcome';
import SettlementPage from './pages/lifecycle/SettlementPage';
import InviteAccept from './pages/lifecycle/InviteAccept';
// Loads the charts library only when opened.
const UsageInsights = lazy(() => import('./pages/admin/UsageInsights'));
import Navbar from './components/Navbar';
import Login from './pages/Login';
import ProtectedRoute from './components/ProtectedRoute';
import { useAmoebaConfig } from './hooks/useAmoebaConfig';

import Home from './pages/Home';
import ImageToPdf from './pages/ImageToPdf';
import MergePdf from './pages/MergePdf';
import SplitPdf from './pages/SplitPdf';
import RotatePdf from './pages/RotatePdf';
import RearrangePdf from './pages/RearrangePdf';
import WatermarkPdf from './pages/WatermarkPdf';
import PdfToJpg from './pages/PdfToJpg';

import Dashboard from './pages/Dashboard';
import ExpenseReportList from './pages/expenses/ExpenseReportList';
import ExpenseReportForm from './pages/expenses/ExpenseReportForm';
import LeaveDashboard from './pages/leaves/LeaveDashboard';
import AdminConfig from './pages/admin/AdminConfig';
import UserManagement from './pages/admin/UserManagement';
import HolidayManagement from './pages/admin/HolidayManagement';
import TimeSetup from './pages/admin/TimeSetup';
import Tracker from './pages/time/Tracker';
import Approvals from './pages/time/Approvals';
import Reports from './pages/time/Reports';
import WorkflowConfig from './pages/admin/WorkflowConfig';
import DepartmentManagement from './pages/admin/DepartmentManagement';
import SystemLogs from './pages/admin/SystemLogs';
import PayrollManagement from './pages/admin/PayrollManagement';
import MySalary from './pages/MySalary';
import Attendance from './pages/Attendance';
import AttendanceManagement from './pages/admin/AttendanceManagement';
import Profile from './pages/Profile';
const HelpCenter = React.lazy(() => import('./pages/HelpCenter'));
import TenantManagement from './pages/platform/TenantManagement';
import SettingsHub from './pages/admin/SettingsHub';
import { ROUTES } from './navigation/modules';
import PlanExpired from './pages/PlanExpired';

// Every signed-in page is guarded by its rule in navigation/modules.js (roles, and the module that must be on).
const guard = (path, element) => <ProtectedRoute rule={ROUTES[path]}>{element}</ProtectedRoute>;

// Layout for authenticated pages (full Navbar)
const PrivateLayout = () => {
    const { config } = useAmoebaConfig();
    const { pathname } = useLocation();
    useEffect(() => { trackPageView(pathname); }, [pathname]); // usage analytics (path only)
    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <Navbar />
            <BugReportModal />
            <HelpLauncher />
            <div style={{ flex: 1 }}>
                <Outlet />
            </div>
            <footer className="flex-center" style={{ marginTop: 'auto', padding: '2rem', color: 'var(--text-muted)' }}>
                <p>© {new Date().getFullYear()} {config.orgName || 'JA Technology Labs'}. All rights reserved.</p>
            </footer>
        </div>
    );
};

function App() {
    return (
        <Router>
            <Routes>
                {/* The organisation's front door: sign in (or straight to the dashboard if already in) */}
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/invite/:token" element={<InviteAccept />} />
                <Route path="/plan-expired" element={<PlanExpired />} />

                {/* Private routes — require auth */}
                <Route element={
                    <ProtectedRoute>
                        <PrivateLayout />
                    </ProtectedRoute>
                }>
                    <Route path="/dashboard" element={guard('/dashboard', <Dashboard />)} />
                    <Route path="/tools/pdf" element={guard('/tools/pdf', <Home />)} />
                    <Route path="/merge-pdf" element={guard('/merge-pdf', <MergePdf />)} />
                    <Route path="/split-pdf" element={guard('/split-pdf', <SplitPdf />)} />
                    <Route path="/rotate-pdf" element={guard('/rotate-pdf', <RotatePdf />)} />
                    <Route path="/rearrange-pdf" element={guard('/rearrange-pdf', <RearrangePdf />)} />
                    <Route path="/watermark-pdf" element={guard('/watermark-pdf', <WatermarkPdf />)} />
                    <Route path="/pdf-to-jpg" element={guard('/pdf-to-jpg', <PdfToJpg />)} />
                    <Route path="/image-to-pdf" element={guard('/image-to-pdf', <ImageToPdf />)} />
                    <Route path="/tools/expenses" element={guard('/tools/expenses', <ExpenseReportList />)} />
                    <Route path="/tools/expenses/new" element={guard('/tools/expenses/new', <ExpenseReportForm />)} />
                    <Route path="/tools/expenses/:id" element={guard('/tools/expenses/:id', <ExpenseReportForm />)} />
                    <Route path="/leaves" element={guard('/leaves', <LeaveDashboard />)} />
                    <Route path="/my-salary" element={guard('/my-salary', <MySalary />)} />
                    <Route path="/attendance" element={guard('/attendance', <Attendance />)} />
                    <Route path="/profile" element={guard('/profile', <Profile />)} />
                    <Route path="/help" element={guard('/help', <React.Suspense fallback={<p style={{ padding: 24 }}>Loading…</p>}><HelpCenter /></React.Suspense>)} />
                    <Route path="/help/:slug" element={guard('/help/:slug', <React.Suspense fallback={<p style={{ padding: 24 }}>Loading…</p>}><HelpCenter /></React.Suspense>)} />
                    <Route path="/bugs" element={guard('/bugs', <MyBugReports />)} />
                    <Route path="/documents" element={guard('/documents', <MyDocuments />)} />
                    <Route path="/tasks" element={guard('/tasks', <MyTasks />)} />
                    <Route path="/welcome" element={guard('/welcome', <Welcome />)} />
                    <Route path="/settlements/:id" element={guard('/settlements/:id', <SettlementPage />)} />
                    <Route path="/admin/settings" element={guard('/admin/settings', <SettingsHub />)} />
                    <Route path="/admin/config" element={guard('/admin/config', <AdminConfig />)} />
                    <Route path="/admin/lifecycle" element={guard('/admin/lifecycle', <LifecycleSetup />)} />
                    <Route path="/admin/checklists" element={guard('/admin/checklists', <CasesAdmin />)} />
                    <Route path="/admin/documents" element={guard('/admin/documents', <DocumentsAdmin />)} />
                    <Route path="/admin/usage" element={guard('/admin/usage', <Suspense fallback={null}><UsageInsights /></Suspense>)} />
                    <Route path="/admin/users" element={guard('/admin/users', <UserManagement />)} />
                    <Route path="/admin/holidays" element={guard('/admin/holidays', <HolidayManagement />)} />
                    {/* Any signed-in user: project managers use it for their own projects; the API enforces who may do what. */}
                    <Route path="/admin/time" element={guard('/admin/time', <TimeSetup />)} />
                    <Route path="/time" element={guard('/time', <Tracker />)} />
                    <Route path="/time/approvals" element={guard('/time/approvals', <Approvals />)} />
                    <Route path="/time/reports" element={guard('/time/reports', <Reports />)} />
                    <Route path="/admin/workflow" element={guard('/admin/workflow', <WorkflowConfig />)} />
                    <Route path="/admin/departments" element={guard('/admin/departments', <DepartmentManagement />)} />
                    <Route path="/admin/logs" element={guard('/admin/logs', <SystemLogs />)} />
                    <Route path="/admin/payroll" element={guard('/admin/payroll', <PayrollManagement />)} />
                    {/* Open to any logged-in user: the API scopes the data to the viewer's
                        reporting tree (ADMIN/HR: whole organisation). */}
                    <Route path="/admin/attendance" element={guard('/admin/attendance', <AttendanceManagement />)} />
                    <Route path="/platform/tenants" element={
                        <ProtectedRoute requireSystemAdmin><TenantManagement /></ProtectedRoute>
                    } />
                </Route>
            </Routes>
        </Router>
    );
}

export default App;
